import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useEventsStore } from 'src/store/benefits/events.store';
import { usePartnersStore } from 'src/store/partners/partners.store';
import { convertFile } from 'src/utils/convert-file.utils';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';
import { isDate } from 'lodash';

const STATUS_OPTIONS = ['active', 'inactive'];
const STATUS_LABEL = ['Ativo', 'Inativo'];

const SCHEDULE_OPTIONS = ['hour', 'day', 'simple', 'external'];
const SCHEDULE_LABEL = ['Dia e horário', 'Somente dia', 'Sem dia e sem horário', 'Externo'];

const TIERS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'silver', label: 'Prata' },
  { value: 'gold', label: 'Ouro' },
  { value: 'diamond', label: 'Diamante' },
];

const CATEGORIES_OPTIONS = [
  { value: 'Infantil', label: 'Infantil' },
  { value: 'Esporte', label: 'Esporte' },
  { value: 'Música', label: 'Música' },
  { value: 'Diversão e lazer', label: 'Diversão e lazer' },
  { value: 'Gastronomia', label: 'Gastronomia' },
];

const AGE_OPTIONS = [
  { value: 'L', label: 'L' },
  { value: '10', label: '10' },
  { value: '12', label: '12' },
  { value: '14', label: '14' },
  { value: '16', label: '16' },
  { value: '18', label: '18' },
];

const HIGHLIGHT_OPTIONS = [
  {
    value: 'without',
    label: 'Sem Destaque',
  },
  {
    value: 'home_highlight',
    label: 'Destaque Home',
  },
];

const PRIVATE_LABEL = ['Privado', 'Público'];
const PRIVATE_OPTIONS = [true, false];

const DONT_LABEL = ['Importados', 'Não importados'];
const DONT_OPTIONS = [true, false];

const LIMITS_LABEL = ['Sim', 'Não'];
const LIMITS_OPTIONS = [true, false];

const TEST_OPTIONS = [true, false];
const TEST_LABEL = ['Sim', 'Não'];

const EXHIBITION_ENV_OPTIONS = [
  { value: 'viva', label: 'VIVA' },
  { value: 'trail', label: 'TRILHA' },
];

const convertBoolToString = (value: boolean) => (value ? 'true' : 'false');

const formatStartDateToRequest = (date: string) => {
  const parsedDate = moment.utc(date, 'YYYY-MM-DD', true);

  return parsedDate.set({ hour: 3, minute: 0, second: 0, millisecond: 0 }).toISOString();
};

const formatEndDateToRequest = (date: string) => {
  const parsedDate = moment.utc(date, 'YYYY-MM-DD', true);

  return parsedDate
    .add(1, 'day')
    .set({ hour: 2, minute: 59, second: 59, millisecond: 999 })
    .toISOString();
};

export default function useEventDetails() {
  const [open, setOpen] = useState<boolean>(false);
  const [days, setDays] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [cpfs, setCpfs] = useState<any[]>([]);
  const [fileNameCpfs, setFileNameCpfs] = useState<string>('');
  const [openCpfs, setOpenCpfs] = useState<boolean>(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const {
    getEventDetails,
    resetEventDetails,
    createUpdateEvent,
    isLoadingEventDetails,
    eventDetails,
  } = useEventsStore();
  const uploadImage = usePartnersStore((state) => state.getUrlFile);
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');

  useEffect(() => {
    if (id) {
      getEventDetails(id);
    } else {
      resetEventDetails();
    }
  }, []);

  const defaultValues = useMemo(
    () => ({
      name: eventDetails?.name || '',
      image: eventDetails?.image || '',
      banners: eventDetails?.banners || [],
      description: eventDetails?.description || '',
      categories: eventDetails?.categories || [],
      local: eventDetails?.local || '',
      duration: eventDetails?.duration || 0,
      info: eventDetails?.info || '',
      startDate: eventDetails?.startDate
        ? moment(eventDetails.startDate).format('YYYY-MM-DD')
        : moment(new Date()).format('YYYY-MM-DD'),
      endDate: eventDetails?.endDate
        ? moment(eventDetails.endDate).format('YYYY-MM-DD')
        : moment(new Date()).format('YYYY-MM-DD'),
      price: eventDetails?.exhibitionEnv === 'trail' ? 0 : eventDetails?.price || 0,
      minAdult: eventDetails?.slotLimits?.minAdult || 0,
      maxAdult: eventDetails?.slotLimits?.maxAdult || 0,
      minChild: eventDetails?.slotLimits?.minChild || 0,
      maxChild: eventDetails?.slotLimits?.maxChild || 0,
      minTotal: eventDetails?.slotLimits?.minTotal || 0,
      maxTotal: eventDetails?.slotLimits?.maxTotal || 0,
      scheduleType: eventDetails?.scheduleType || 'hour',
      externalUrl: eventDetails?.externalUrl || '',
      externalCouponLink: eventDetails?.externalCouponLink || '',
      status: eventDetails?.status || 'active',
      exhibitionEnv: eventDetails?.exhibitionEnv === 'trail' ? 'trail' : 'viva',
      ageRating: eventDetails?.ageRating || 'L',
      visibleForTiers:
        eventDetails?.exhibitionEnv === 'trail' ||
        !eventDetails?.visibleForTiers ||
        eventDetails?.visibleForTiers?.length === 0
          ? ['all']
          : eventDetails?.visibleForTiers,
      highlightCategory: Array.isArray(eventDetails?.highlightCategory)
        ? eventDetails?.highlightCategory
        : ['without'],
      totalSlots:
        eventDetails?.eventSlots?.length! > 0 ? (eventDetails?.eventSlots[0]?.slots as number) : 0,
      private: !!eventDetails?.private,
      dontShowToImported: !!eventDetails?.dontShowToImported,
      userPurchaseLimit: eventDetails?.userPurchaseLimit.limit || 1,
      hasSlotLimits: isEdit ? convertBoolToString(!!eventDetails?.hasSlotLimits) : 'true',
      test: eventDetails?.test || false,
      priority: eventDetails?.priority || 0,
      hasCustomerVoice: !!(
        eventDetails?.customerVoiceData?.baseUrl ||
        eventDetails?.customerVoiceData?.assetsUrl ||
        eventDetails?.customerVoiceData?.customerVoiceFormId
      ),
      customerVoiceData: {
        baseUrl: eventDetails?.customerVoiceData?.baseUrl || '',
        assetsUrl: eventDetails?.customerVoiceData?.assetsUrl || '',
        customerVoiceFormId: eventDetails?.customerVoiceData?.customerVoiceFormId || '',
      },
    }),
    [eventDetails, isEdit]
  );

  const eventSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório'),
    image: Yup.mixed().required('Campo obrigatório'),
    highlightCategory: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    banners: Yup.mixed(),
    description: Yup.string().required('Campo obrigatório'),
    categories: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    local: Yup.string().required('Campo obrigatório'),
    duration: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    info: Yup.string().required('Campo obrigatório'),
    startDate: Yup.string().when('exhibitionEnv', {
      is: 'trail',
      then: (startDateSchema) => startDateSchema.nullable().notRequired(),
      otherwise: (startDateSchema) => startDateSchema.required('Data de início é obrigatória'),
    }),
    endDate: Yup.string().when('exhibitionEnv', {
      is: 'trail',
      then: (endDateSchema) => endDateSchema.nullable().notRequired(),
      otherwise: (endDateSchema) =>
        endDateSchema
          .required('Data de término é obrigatória')
          .test(
            'is-minus-than-start',
            'Data de término deve ser maior que a data de início',
            (v, context) => {
              const { startDate } = context.parent;
              const start = new Date(startDate);
              const end = new Date(v);
              return end.getTime() >= start.getTime();
            }
          ),
    }),
    price: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    minAdult: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    maxAdult: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    minChild: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    maxChild: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    minTotal: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    maxTotal: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    scheduleType: Yup.string(),
    externalUrl: Yup.string(),
    externalCouponLink: Yup.string(),
    status: Yup.string(),
    exhibitionEnv: Yup.string().required('Campo obrigatório'),
    ageRating: Yup.string().required('Campo obrigatório'),
    visibleForTiers: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    totalSlots: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    userPurchaseLimit: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(1, 'O número deve ser maior ou igual a 1!'),
    test: Yup.boolean(),
    priority: Yup.number(),
    hasCustomerVoice: Yup.boolean().required(),
    customerVoiceData: Yup.object({
      baseUrl: Yup.string(),
      assetsUrl: Yup.string(),
      customerVoiceFormId: Yup.string(),
    })
      .when('hasCustomerVoice', {
        is: (hasCustomerVoice: boolean) => hasCustomerVoice,
        then: (customerVoiceSchema) =>
          customerVoiceSchema.shape({
            baseUrl: Yup.string().required('Campo obrigatório'),
            assetsUrl: Yup.string().required('Campo obrigatório'),
            customerVoiceFormId: Yup.string().required('Campo obrigatório'),
          }),
        otherwise: (customerVoiceSchema) =>
          customerVoiceSchema.shape({
            baseUrl: Yup.string().notRequired(),
            assetsUrl: Yup.string().notRequired(),
            customerVoiceFormId: Yup.string().notRequired(),
          }),
      })
      .required(),
  });

  const methods = useForm({
    resolver: yupResolver(eventSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const handleDrop = useCallback(
    (acceptedFiles: any) => {
      const file = acceptedFiles[0];
      if (file) {
        setValue(
          'image',
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [eventDetails, reset, defaultValues]);

  const resolveUploadedImage = async (file: File | string, uploadErrorMessage: string) => {
    const [image, format] = await convertFile(file);

    if (format === 'error') {
      throw new Error('Ocorreu um erro ao preparar a imagem.');
    }

    if (format === 'base64') {
      const uploaded = await uploadImage(image);

      if (uploaded === 'error') {
        throw new Error(uploadErrorMessage);
      }

      return uploaded;
    }

    return image;
  };

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const isTrailEnv = data.exhibitionEnv === 'trail';
      const startDate = isTrailEnv ? null : formatStartDateToRequest(data.startDate);
      const endDate = isTrailEnv ? null : formatEndDateToRequest(data.endDate);
      let daysToRegister = [];
      if (['hour', 'day'].includes(data.scheduleType)) {
        daysToRegister = days.length > 0 ? days : [];
      } else if (data.scheduleType === 'simple' && !isTrailEnv) {
        daysToRegister = [
          {
            date: `${moment(data.startDate).format('YYYY-MM-DD')}T00:00:00.000-03:00`,
            slots: data.totalSlots,
          },
        ];
      }

      const imageUrl = await resolveUploadedImage(data.image, 'Erro ao fazer upload da imagem.');
      const bannerUrls = await Promise.all(
        (Array.isArray(data.banners) ? data.banners : []).map((banner: File | string) =>
          resolveUploadedImage(banner, 'Erro ao fazer upload do banner.')
        )
      );

      const eventToRegister = {
        id: id || null,
        name: data.name,
        image: imageUrl,
        banners: bannerUrls,
        description: data.description,
        categories: data.categories,
        local: data.local,
        duration: data.scheduleType === 'external' ? 0 : data.duration,
        info: data.info,
        startDate,
        endDate,
        price: isTrailEnv ? 0 : data.price,
        expiration: {
          expirationType: 'date',
          expirationDate: endDate,
        },
        slotLimits: {
          minAdult: data.minAdult,
          maxAdult: data.maxAdult,
          minChild: data.minChild,
          maxChild: data.maxChild,
          minTotal: data.minTotal,
          maxTotal: data.maxTotal,
        },
        scheduleType: data.scheduleType,
        externalUrl: data.scheduleType === 'external' ? data.externalUrl : null,
        externalCouponLink: data.externalCouponLink || null,
        status: data.status,
        exhibitionEnv: isTrailEnv ? 'trail' : 'viva',
        ageRating: data.ageRating,
        visibleForTiers:
          isTrailEnv ||
          (Array.isArray(data.visibleForTiers) &&
            data.visibleForTiers.length === 1 &&
            data.visibleForTiers[0] === 'all')
            ? []
            : data.visibleForTiers,
        highlightCategory: data.highlightCategory,
        days: daysToRegister,
        private: isTrailEnv ? null : data.private,
        dontShowToImported: isTrailEnv ? null : data.dontShowToImported,
        cpfs: isTrailEnv ? null : cpfs,
        userPurchaseLimit: {
          limit: data.userPurchaseLimit,
        },
        hasSlotLimits: data.hasSlotLimits,
        test: data.test,
        priority: data.priority,
        customerVoiceData: data.hasCustomerVoice ? data.customerVoiceData : null,
      };
      await createUpdateEvent(eventToRegister as any);
      enqueueSnackbar(isEdit ? 'Evento atualizado com sucesso' : 'Evento cadastrado com sucesso');
      navigate(paths.dashboard.eventsList);
    } catch (error) {
      const fallbackMessage = isEdit ? 'Erro ao atualizar evento' : 'Erro ao criar evento';
      enqueueSnackbar(error instanceof Error ? error.message : fallbackMessage, {
        variant: 'error',
      });
      console.error(error);
    }
  });

  const handleDropMultiple = useCallback(
    (acceptedFiles: any[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const newBanners = acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );

        setValue('banners', [...(values.banners || []), ...newBanners], {
          shouldValidate: true,
        });
      }
    },
    [setValue, values.banners]
  );

  const handleRemove = (fileToRemove: string) => {
    const filteredBanners = values?.banners?.filter((banner: any) => banner !== fileToRemove);
    setValue('banners', filteredBanners as any, { shouldValidate: true });
  };

  const handleRemoveAll = () => {
    setValue('banners', []);
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      const readedData = XLSX.read(data, { type: 'binary', cellDates: true });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];
      const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const formattedData: any = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        raw: false,
        dateNF: 'DD/MM/YYYY',
      });

      if (
        values.scheduleType === 'hour' &&
        (dataParse?.[0]?.[0] !== 'Dia' ||
          dataParse?.[0]?.[1] !== 'Hora' ||
          dataParse?.[0]?.[2] !== 'Vagas')
      ) {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      if (
        values.scheduleType === 'day' &&
        (dataParse?.[0]?.[0] !== 'Dia' || dataParse?.[0]?.[1] !== 'Vagas')
      ) {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      const daysToInsert: any[] = [];
      const datesLines = dataParse?.slice(1);
      const formattedDatesLines = formattedData?.slice(1);

      datesLines.forEach((line: any, index: number) => {
        const formattedLine = formattedDatesLines[index] || [];
        const parsedDate = moment(
          String(formattedLine[0] || ''),
          ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD'],
          true
        );
        const date = parsedDate.isValid() ? parsedDate : moment(line[0]);
        const parsedTime = moment(
          String(formattedLine[1] || ''),
          ['H:mm', 'HH:mm', 'H:mm:ss', 'HH:mm:ss'],
          true
        );
        const time = parsedTime.isValid() ? parsedTime : moment(line[1]);

        if (values.scheduleType === 'day' && (parsedDate.isValid() || isDate(line[0]))) {
          daysToInsert.push({
            date: `${date.format('YYYY-MM-DD')}T00:00:00.000-03:00`,
            slots: line[1],
          });
        } else if (
          (parsedDate.isValid() || isDate(line[0])) &&
          (parsedTime.isValid() || isDate(line[1]))
        ) {
          daysToInsert.push({
            date: `${date.format('YYYY-MM-DD')}T${time.format('HH:mm')}:00.000-03:00`,
            slots: line[2],
          });
        }
      });

      setFileName(file.name);
      setDays(daysToInsert);
      if (datesLines.length !== daysToInsert.length) {
        enqueueSnackbar(
          'Alguns horários não foram importados, verifique a formatação da planilha!',
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar('Horários adicionados com Sucesso!');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleUploadCpfs = (e: any) => {
    e.preventDefault();
    const { files } = e.target;
    // eslint-disable-next-line
    var f = files[0];

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      const readedData = XLSX.read(data, { type: 'binary', cellDates: true });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];
      const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (dataParse?.[0]?.[0] !== 'Cpfs abaixo') {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      const cpfsToInsert: any[] = [];
      const datesLines = dataParse?.slice(1);
      datesLines.forEach((line: any) => {
        cpfsToInsert.push(line[0]);
      });
      setFileNameCpfs(f.name);
      setCpfs(cpfsToInsert);
      enqueueSnackbar('Cpfs adicionados com Sucesso!');
    };
    reader.readAsBinaryString(f);
  };

  const handleDownloadDays = useCallback(() => {
    const daysSource = days.length ? days : eventDetails?.eventSlots || [];

    if (!daysSource.length) {
      return;
    }

    const normalizedDays = daysSource.map((day: any) => {
      if (Array.isArray(day)) {
        return {
          date: day[0],
          time: day[1],
          slots: day[2] ?? day[1],
        };
      }

      return {
        date: day?.date,
        time: day?.time,
        slots: day?.slots,
      };
    });

    const worksheet =
      values.scheduleType === 'hour'
        ? XLSX.utils.aoa_to_sheet([
            ['Dia', 'Hora', 'Vagas'],
            ...normalizedDays.map(({ date, time, slots }) => [
              moment(date).format('YYYY-MM-DD'),
              time
                ? moment(time, ['HH:mm', moment.ISO_8601]).format('HH:mm')
                : moment(date).format('HH:mm'),
              slots,
            ]),
          ])
        : XLSX.utils.aoa_to_sheet([
            ['Dia', 'Vagas'],
            ...normalizedDays.map(({ date, slots }) => [moment(date).format('YYYY-MM-DD'), slots]),
          ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dias');

    XLSX.writeFile(workbook, `dias_evento_${id || new Date().getTime()}.xlsx`);
  }, [days, eventDetails?.eventSlots, id, values.scheduleType]);

  const handleDownloadCpfs = useCallback(() => {
    const cpfsToDownload = cpfs
      .map((cpf) => (Array.isArray(cpf) ? cpf?.[0] : cpf))
      .filter(Boolean)
      .map((cpf) => String(cpf));

    if (!cpfsToDownload.length) {
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet([
      ['Cpfs abaixo'],
      ...cpfsToDownload.map((cpf) => [cpf]),
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cpfs');

    XLSX.writeFile(workbook, `cpfs_experiencia_${id || new Date().getTime()}.xlsx`);
  }, [cpfs, id]);

  useEffect(() => {
    if (
      values?.highlightCategory &&
      values?.highlightCategory?.includes('without') &&
      values.highlightCategory.length > 1
    ) {
      setValue('highlightCategory', ['without']);
    }
  }, [values.highlightCategory]);

  useEffect(() => {
    if (values.exhibitionEnv === 'trail') {
      if (values.price !== 0) {
        setValue('price', 0);
      }

      if (
        !Array.isArray(values.visibleForTiers) ||
        values.visibleForTiers.length !== 1 ||
        values.visibleForTiers[0] !== 'all'
      ) {
        setValue('visibleForTiers', ['all']);
      }
      return;
    }

    if (
      Array.isArray(values.visibleForTiers) &&
      values.visibleForTiers?.includes('all') &&
      values.visibleForTiers.length > 1
    ) {
      setValue('visibleForTiers', ['all']);
    }
  }, [setValue, values.exhibitionEnv, values.price, values.visibleForTiers]);

  useEffect(() => {
    if (eventDetails?.cpfs) {
      setCpfs(eventDetails.cpfs);
    }
  }, [eventDetails]);

  const onCloseCpfs = () => {
    setOpenCpfs(false);
  };

  const hasCpfsToDownload = useMemo(() => isEdit && cpfs && cpfs.length > 0, [cpfs]);
  const hasDaysToDownload = useMemo(
    () =>
      isEdit &&
      ((Array.isArray(days) && days.length > 0) ||
        (Array.isArray(eventDetails?.eventSlots) && eventDetails?.eventSlots.length > 0)),
    [days, eventDetails?.eventSlots, isEdit]
  );

  return {
    isEdit,
    isLoadingEventDetails,
    methods,
    values,
    isSubmitting,
    STATUS_OPTIONS,
    STATUS_LABEL,
    TIERS_OPTIONS,
    CATEGORIES_OPTIONS,
    SCHEDULE_OPTIONS,
    SCHEDULE_LABEL,
    AGE_OPTIONS,
    HIGHLIGHT_OPTIONS,
    PRIVATE_LABEL,
    PRIVATE_OPTIONS,
    DONT_LABEL,
    DONT_OPTIONS,
    LIMITS_LABEL,
    LIMITS_OPTIONS,
    open,
    days,
    fileName,
    id,
    cpfs,
    fileNameCpfs,
    openCpfs,
    TEST_OPTIONS,
    TEST_LABEL,
    EXHIBITION_ENV_OPTIONS,
    hasCpfsToDownload,
    hasDaysToDownload,
    handleDownloadDays,
    handleDownloadCpfs,
    onCloseCpfs,
    setOpenCpfs,
    setDays,
    setOpen,
    onSubmit,
    handleDrop,
    handleDropMultiple,
    handleRemove,
    handleRemoveAll,
    navigate,
    enqueueSnackbar,
    handleUpload,
    handleUploadCpfs,
    eventDetails,
  };
}
